import {
  Box,
  Flex,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  Text,
} from '@chakra-ui/react';
import React from 'react';

import dayjs from 'lib/date/dayjs';
import useIsMounted from 'lib/hooks/useIsMounted';
import useMultichainChainsConfig from 'lib/multichain/useMultichainChainsConfig';
import useMultichainPortfolio from 'lib/multichain/useMultichainPortfolio';
import IconSvg from 'ui/shared/IconSvg';

type Props = {
  addressHash: string;
};

const MultichainInfo = ({ addressHash }: Props) => {
  const isMounted = useIsMounted();
  const chainsQuery = useMultichainChainsConfig({ enabled: isMounted });
  const portfolioQuery = useMultichainPortfolio({
    addressHash,
    chainsConfig: chainsQuery.data,
    enabled: isMounted && chainsQuery.isSuccess,
  });

  const chainsConfig = chainsQuery.data;
  const portfolio = portfolioQuery.data;

  const chainMetaById = React.useMemo(() => {
    if (!chainsConfig) {
      return new Map();
    }

    return new Map(chainsConfig.chains.map((chain) => [ chain.id, chain ]));
  }, [ chainsConfig ]);

  if (!isMounted) {
    return <Skeleton height="24px" width="280px"/>;
  }

  if (chainsQuery.isError || portfolioQuery.isError) {
    return null;
  }

  if (chainsQuery.isPending || portfolioQuery.isPending) {
    return <Skeleton height="24px" width="280px"/>;
  }

  if (!portfolio || !chainsConfig) {
    return null;
  }

  const chainsLabel = portfolio.chains_count === 1 ? 'chain' : 'chains';
  const staleLabel = portfolio.stale && portfolio.cached_at
    ? `As of ${ dayjs(portfolio.cached_at).format('MMM D, HH:mm UTC') } · may be outdated`
    : null;

  return (
    <Box>
      <Flex
        alignItems="center"
        gap={ 2 }
        flexWrap="wrap"
        px={ 3 }
        py={ 2 }
        borderRadius="full"
        bg="blackAlpha.50"
        _dark={{ bg: 'whiteAlpha.100' }}
        fontSize="sm"
        fontWeight={ 600 }
        w="fit-content"
      >
        <IconSvg name="tokens" boxSize={ 5 } color="gray.500"/>
        <Text>${ Number(portfolio.total_usd).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }</Text>
        <Text fontWeight={ 500 } color="gray.500">(Multichain Portfolio)</Text>
      </Flex>

      { staleLabel && (
        <Text mt={ 1 } fontSize="xs" color="gray.500">{ staleLabel }</Text>
      ) }

      <Flex mt={ 2 } alignItems="center" gap={ 1 } fontSize="sm">
        <Text color="gray.600" _dark={{ color: 'gray.400' }}>
          { portfolio.chains_count } addresses on
        </Text>
        <Menu>
          <MenuButton
            as={ Text }
            color="link"
            fontWeight={ 500 }
            cursor="pointer"
            _hover={{ textDecoration: 'underline' }}
          >
            { portfolio.chains_count } { chainsLabel }
            <IconSvg name="arrows/east" boxSize={ 4 } transform="rotate(90deg)" display="inline-block" ml={ 1 }/>
          </MenuButton>
          <MenuList maxH="240px" overflowY="auto">
            { portfolio.chains.map((item) => {
              const meta = chainMetaById.get(item.chain);
              const usd = Number(item.usd_total).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
              const explorerHref = meta?.explorer_url
                ? `${ meta.explorer_url.replace(/\/$/, '') }${ meta.explorer_address_path || '/address' }/${ addressHash }`
                : undefined;

              const itemProps = explorerHref
                ? { as: 'a' as const, href: explorerHref, target: '_blank', rel: 'noopener noreferrer' }
                : {};

              return (
                <MenuItem key={ item.chain } fontSize="sm" { ...itemProps }>
                  <Flex alignItems="center" justifyContent="space-between" width="100%" gap={ 3 }>
                    <Flex alignItems="center" gap={ 2 } minW={ 0 }>
                      { meta?.logo_url && (
                        <Image src={ meta.logo_url } alt={ meta.explorer_label } boxSize={ 5 } borderRadius="full"/>
                      ) }
                      <Text isTruncated>{ meta?.explorer_label || item.chain }</Text>
                      { explorerHref && (
                        <IconSvg name="arrows/north-east" boxSize={ 3 } color="gray.500" flexShrink={ 0 }/>
                      ) }
                    </Flex>
                    <Text flexShrink={ 0 } color="gray.600">(${ usd })</Text>
                  </Flex>
                </MenuItem>
              );
            }) }
          </MenuList>
        </Menu>
      </Flex>
    </Box>
  );
};

export default MultichainInfo;
